import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { Terminal, Warehouse } from '../models/terminal.model';
import { OrdersApiService } from './orders-api.service';
import { ProductApiService } from './product-api.service';

@Injectable({
  providedIn: 'root'
})
export class TerminalsApiService {

  $terminals = new BehaviorSubject<Terminal[]>([]);
  terminals$ = this.$terminals.pipe(
    map(terminals =>
      // grab country code from a warehouse. assume all warehouses in a terminal are in the same country by trusting the first one
      terminals.map(terminal =>
        Object.assign({}, terminal, { countryCode: terminal.warehouses.map(warehouse => warehouse.address?.countryCode).shift() })
      )
    )
  );
  currentTerminalId$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  warehouses$ = combineLatest([this.$terminals.pipe(startWith([])), this.currentTerminalId$])
    .pipe(
      map(([terminals, id]) => terminals.find(terminal => terminal.groupCode === id)),
      filter(terminal => !!terminal),
      map(terminal => terminal.warehouses.sort((a: Warehouse, b: Warehouse) => a.name > b.name ? 1 : -1)),
      startWith([] as Warehouse[]),
      shareReplay(1)
    );

  currentCountryCode$ = combineLatest([this.terminals$, this.currentTerminalId$])
    .pipe(
      map(([terminals, groupCode]) => {
        const terminal = terminals.find(t => t.groupCode === groupCode);
        return terminal?.countryCode;
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  lastUpdate = 0;

  constructor(
    private http: HttpClient,
    private productsApiService: ProductApiService,
    private ordersApiService: OrdersApiService,
  ) {
    this.update();

    combineLatest([
      this.currentTerminalId$.pipe(filter(x => !!x), distinctUntilChanged()),
      this.ordersApiService.orderSent.pipe(startWith(false))
    ])
      .subscribe(([terminalId]) => {
        this.productsApiService.fetch(terminalId);
      });
  }

  setCurrentTerminal(terminalId: string) {
    this.currentTerminalId$.next(terminalId);
  }

  getCurrentTerminal() {
    return this.currentTerminalId$.getValue();
  }

  update() {
    const now = Date.now();
    if (now - this.lastUpdate > 60 * 1000) {
      this.lastUpdate = now;
      this.fetch();
    }
  }

  fetch() {
    this.loading$.next(true);
    this.http.get<Terminal[]>(`${env.apiPath}/terminals/`)
      .toPromise()
      .then(data => {
        this.$terminals.next(data);
        this.loading$.next(false);
      })
      .catch(error => {
        this.loading$.next(false);
      });
  }
}
